import * as React from "react";
import { cn } from "../../lib/utils";

type StatVariant = "total" | "positive" | "negative" | "neutral";

interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: StatVariant;
}

interface StatCardChangeProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  variant?: "positive" | "negative" | "neutral";
}

const VARIANT_CLASSES: Record<StatVariant, string> = {
  total:
    "border-blue-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.blue.100/.7),theme(colors.blue.50/.5)_40%,transparent_70%)]",
  positive:
    "border-green-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.green.100/.7),theme(colors.green.50/.5)_40%,transparent_70%)]",
  negative:
    "border-red-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.rose.100/.7),theme(colors.rose.50/.5)_40%,transparent_70%)]",
  neutral:
    "border-slate-200 bg-[radial-gradient(120%_120%_at_0%_0%,theme(colors.slate.100/.65),theme(colors.slate.50/.45)_40%,transparent_70%)]",
};

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, variant = "total", ...props }, ref) => {
    const gradientClasses = VARIANT_CLASSES[variant];
    const isClickable = typeof props.onClick === "function";

    return (
      <div
        ref={ref}
        data-slot="card"
        className={cn(
          "relative rounded-xl border shadow-sm transition-shadow bg-card text-card-foreground",
          "hover:shadow-md",
          isClickable && "cursor-pointer",
          gradientClasses,
          "before:absolute before:inset-0 before:rounded-[inherit] before:pointer-events-none before:opacity-60",
          "before:bg-gradient-to-b before:from-white/10 before:to-transparent",
          "after:absolute after:inset-0 after:rounded-[inherit] after:pointer-events-none after:opacity-[0.25]",
          "after:[background-image:radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.05)_1px,transparent_1px)] after:[background-size:10px_10px]",
          "dark:before:from-white/5 dark:after:opacity-[0.15]",
          className
        )}
        {...props}
      />
    );
  }
);
StatCard.displayName = "StatCard";

export const StatCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-row items-center justify-between space-y-0 pl-6 pt-6 pr-6 pb-2",
      className
    )}
    {...props}
  />
));
StatCardHeader.displayName = "StatCardHeader";

export const StatCardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("text-sm font-medium relative z-10", className)}
    {...props}
  />
));
StatCardTitle.displayName = "StatCardTitle";

export const StatCardIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("h-4 w-4 text-muted-foreground relative z-10", className)}
    {...props}
  />
));
StatCardIcon.displayName = "StatCardIcon";

export const StatCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("px-6 pb-6 relative z-10", className)}
    {...props}
  />
));
StatCardContent.displayName = "StatCardContent";

export const StatCardValue = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-2xl font-bold", className)}
    {...props}
  />
));
StatCardValue.displayName = "StatCardValue";

export const StatCardChange = React.forwardRef<
  HTMLParagraphElement,
  StatCardChangeProps
>(({ className, variant = "positive", ...props }, ref) => {
  const changeClasses: Record<
    NonNullable<StatCardChangeProps["variant"]>,
    string
  > = {
    positive: "text-green-500",
    negative: "text-red-500",
    neutral: "text-muted-foreground",
  };

  return (
    <p
      ref={ref}
      className={cn("text-xs", changeClasses[variant], className)}
      {...props}
    />
  );
});
StatCardChange.displayName = "StatCardChange";
